/* eslint-disable @typescript-eslint/camelcase */
import { Component } from 'vue-property-decorator'
import { Default, FormStructure, Options, TableFormStructure } from '@master/model/structure/FormStructure'
import { FormBaseMixin } from './FormBaseMixin'
import { BscGrade, BscGrading, FitAndProper, FitProperFields, SvBscGrade } from '@core/model/recruit/forms/FitAndProper'
import { DeclarationOptions } from '@core/model/recruit/forms/GeneralDeclaration'
import { getOptions } from '@core/annotation/Selection'

type DeclarationFieldTypes = FitProperFields | BscGrading

@Component
export class FitAndProperMixin extends FormBaseMixin {
  protected type = FitAndProper
  private declarationConfig: Default<DeclarationFieldTypes> | Options<DeclarationFieldTypes>
  = { type: 'smart', optionCols: 4, options: DeclarationOptions }

  get fields () {
    return this.formData.data as FitProperFields
  }

  get companyName () {
    return this.projectInfo.companyShortName
  }

  get fitProperStructure (): FormStructure<FitProperFields> {
    return [
      {
        label: 'Have you been licensed, registered or approved under any law in any jurisdiction which requires licensing, registration or approval in relation to any regulated activity?',
        key: 1,
        ...this.declarationConfig
      },
      {
        label: 'Have you been refused the right or restricted in your right to carry on any trade, business or profession for which a specific licence, registration or other authorization is required by law in any jurisdiction?',
        key: 2,
        ...this.declarationConfig
      },
      {
        label: 'Have you been issued a prohibition order under any Act administered by Monetary Authority of Singapore or have you been prohibited from operating in any jurisdiction by any financial services regulatory authority?',
        key: 3,
        ...this.declarationConfig
      },
      {
        label: 'Have you been a shareholder of any corporation which holds membership of any class or description of any operator of a market or clearing facility in any jurisdiction?',
        key: 4,
        ...this.declarationConfig
      },
      {
        label: 'Have you been censured, disciplined, suspended or refused membership or registration by Monetary Authority of Singapore, any other regulatory authority, an operator of a market or clearing facility, any professional body or government agency, whether in Singapore or elsewhere?',
        key: 5,
        ...this.declarationConfig
      },
      {
        label: 'Have you been the subject of any complaint made reasonably and in good faith, relating to activities that are regulated by Monetary Authority of Singapore or under any law in any jurisdiction?',
        key: 6,
        ...this.declarationConfig
      },
      {
        label: 'Have you ever been reported to the MAS pursuant to Financial Advisers Act (FAA) Notice on Reporting of Misconduct of Representatives by Financial Advisers, or equivalent provisions under Securities & Futures Act (SFA), or any other Financial Services, Securities, Insurance or Banking Regulations?',
        key: 7,
        ...this.declarationConfig
      },
      {
        label: 'Have you been reported and/or issued Letter of Suspension/Warning/Advice by your past employers due to any misconducts or offences?',
        key: 8,
        ...this.declarationConfig
      },
      {
        label: 'Have you been the subject of any proceedings of a disciplinary or criminal nature or have you been notified of any potential proceedings or of any investigation which might lead to those proceedings, under any law in any jurisdiction?',
        key: 9,
        ...this.declarationConfig
      },
      {
        label: 'Have you been convicted of any offence or are you being subject to any pending proceedings which may lead to such a conviction, under any law in any jurisdiction?',
        key: 10,
        ...this.declarationConfig
      },
      {
        label: 'Have you had any judgment (in particular,that associated with a finding of fraud, misrepresentation or dishonesty) entered against you in any civil proceedings or a party to any pending proceedings which may lead to such a judgment, under any law in any jurisdiction?',
        key: 11,
        ...this.declarationConfig
      },
      {
        label: 'Have you accepted civil liability for fraud, misrepresentation or dishonesty under any law in any jurisdiction?',
        key: 12,
        ...this.declarationConfig
      },
      {
        label: 'Have you had any civil penalty enforcement action taken against you by Monetary Authority of Singapore or any other regulatory authority under any law in any jurisdiction?',
        key: 13,
        ...this.declarationConfig
      },
      {
        label: 'Have you contravened or abetted another person in breach of any laws or regulations, business rules or codes of conduct, whether in Singapore or elsewhere?',
        key: 14,
        ...this.declarationConfig
      },
      {
        label: 'Have you been the subject of any investigations or disciplinary proceedings or been issued a warning or reprimand by Monetary Authority of Singapore, any other regulatory authority, an operator of a market or clearing facility, any professional body or government agency, whether in Singapore or elsewhere?',
        key: 15,
        ...this.declarationConfig
      },
      {
        label: 'Have you been refused a fidelity or surety bond, whether in Singapore or elsewhere?',
        key: 16,
        ...this.declarationConfig
      },
      {
        label: 'Have you demonstrated an unwillingness to comply with any regulatory requirement or to uphold any professional and ethical standards, whether in Singapore or elsewhere?',
        key: 17,
        ...this.declarationConfig
      },
      {
        label: 'Have you been untruthful or provided false or misleading information to Monetary Authority of Singapore or been uncooperative in any dealings with Monetary Authority of Singapore or any other regulatory authority in any jurisdiction?',
        key: 18,
        ...this.declarationConfig
      },
      {
        label: 'Are you or have you been a director, partner, substantial shareholder or concerned in the management of a business that has been censured, disciplined, prosecuted or convicted of a criminal offence, or been the subject of any disciplinary or criminal investigation or proceeding, in Singapore or elsewhere, in relation to any matter that took place while you were a director, partner, substantial shareholder or concerned in the management of the business?',
        key: 19,
        ...this.declarationConfig
      },
      {
        label: 'Are you or have you been a director, partner, substantial shareholder or concerned in the management of a business that has been suspended or refused membership or registration by Monetary Authority of Singapore, any other regulatory authority, an operator of a market or clearing facility, any professional body or government agency, whether in Singapore or elsewhere?',
        key: 20,
        ...this.declarationConfig
      },
      {
        label: 'Have you been a director, partner, substantial shareholder or concerned in the management of a business that has gone into insolvency, liquidation or administration during the period when, or within a period of one year after, you were a director, partner, substantial shareholder or concerned in the management of the business, whether in Singapore or elsewhere?',
        key: 21,
        ...this.declarationConfig
      },
      {
        label: 'Have you been dismissed or asked to resign from office, employment, a position of trust or a fiduciary appointment or similar position, whether in Singapore or elsewhere?',
        key: 22,
        ...this.declarationConfig
      },
      {
        label: 'Are you or have you been subject to disciplinary proceedings by your current or former employer(s), whether in Singapore or elsewhere?',
        key: 23,
        ...this.declarationConfig
      },
      {
        label: 'Have you been disqualified from acting as a director or disqualified from acting in any managerial capacity, whether in Singapore or elsewhere?',
        key: 24,
        ...this.declarationConfig
      },
      {
        label: 'Have you been an officer found liable for an offence committed by a body corporate as a result of the offence having proved to have been committed with the consent or connivance of, or neglect attributable to you, whether in Singapore or elsewhere?',
        key: 25,
        ...this.declarationConfig
      },
      {
        label: 'Are you carrying on business in, or are you acting as a representative in respect of, providing credit rating services, are you or have you been in observance of the Code of Conduct for Credit Rating Agencies.',
        key: 26,
        ...this.declarationConfig
      }
    ]
  }

  get financialSoundStructure (): FormStructure<FitProperFields> {
    return [
      {
        label: 'Are you or have you been unable to fulfil any of your financial obligations, whether in Singapore or elsewhere?',
        key: 27,
        ...this.declarationConfig
      },
      {
        label: 'Have you been placed under the Debt Repayment Scheme (DRS), entered into a compromise or scheme of arrangement with your creditors or made an assignment for the benefit of your creditors, being a compromise, scheme of arrangement or assignment that is still in operation, whether in Singapore or elsewhere?',
        key: 28,
        ...this.declarationConfig
      },
      {
        label: 'Are you subject to a judgment debt which is unsatisfied, either in whole or in part, whether in Singapore or elsewhere?',
        key: 29,
        ...this.declarationConfig
      },
      {
        label: 'Are you or have you been the subject of a bankruptcy petition, whether in Singapore or elsewhere?',
        key: 30,
        ...this.declarationConfig
      },
      {
        label: 'Have you been adjudicated a bankrupt and the bankruptcy is undischarged, whether in Singapore or elsewhere?',
        key: 31,
        ...this.declarationConfig
      },
      {
        label: 'Are you or have you been subject to any other process outside Singapore that is similar to those referred to in Question 30 and 31?',
        key: 32,
        ...this.declarationConfig
      },
      {
        label: 'Have you been engaged in any activity, practice or conduct which would constitute tax evasion* or tax evasion facilitation offence**?',
        key: 'taxOffence',
        ...this.declarationConfig
      },
      {
        label: 'Please specify offence',
        key: 'taxOffenceSpecified',
        show: (this.formData as FitAndProper).data.taxOffence === 'yes'
      }
    ]
  }

  get bscStructure (): FormStructure<BscGrading> {
    return [
      {
        label: 'Have you been accorded BSC Grade ‘E’ as a representative or “Unsatisfactory” as a supervisor?',
        key: 'unsatisfactory',
        ...this.declarationConfig
      }
    ]
  }

  get repBscCols (): TableFormStructure<BscGrade> {
    return [
      { label: 'Quarter / Year', key: 'period', tip: 'e.g. Q1’17', type: 'text' },
      {
        label: 'Grade as a representative',
        key: 'grade',
        type: 'smart',
        optionCols: 6,
        options: getOptions(this.fields.bscGrading.rep[0], 'grade')
      }
    ]
  }

  get repBscError () {
    return []
  }

  get svBscCols (): TableFormStructure<SvBscGrade> {
    return [
      { label: 'Quarter / Year', key: 'period', tip: 'e.g. Q1’17', type: 'text' },
      {
        label: 'Grade as a supervisor',
        key: 'grade',
        type: 'smart',
        optionCols: 4,
        options: getOptions(this.fields.bscGrading.sv[0], 'grade')
      }
    ]
  }

  get additionalStructure (): FormStructure<FitProperFields> {
    return [
      {
        label: 'If the answer to any of the above questions is in the affirmative, please provide more information below and supporting documents, if any',
        key: 'additional',
        type: 'textarea'
      }
    ]
  }

  get svBscError () {
    return []
  }
}
